/* eslint-disable max-nested-callbacks */
import { useCallback, useEffect, useRef } from 'react';
import qz from 'qz-tray';
import useLocalStorageState from 'use-local-storage-state';

import { getCertificate, getSignature } from '~services/Cert/service';

const printersStateKey = 'printersState';
export const printerStatus = {
  disconnected: 'disconnected',
  connected: 'connected',
  connecting: 'connecting'
};
function usePrinter() {
  const initialStatusConnected = qz.websocket.isActive();
  const [printersState, setPrinterState] = useLocalStorageState(printersStateKey, {
    defaultValue: {
      status: initialStatusConnected ? printerStatus.connected : printerStatus.disconnected,
      printers: []
    }
  });

  const isMountedRef = useRef(true);

  useEffect(() => {
    qz.security.setCertificatePromise(function (resolve, reject) {
      return getCertificate().then(resolve).catch(reject);
    });
    qz.security.setSignatureAlgorithm('SHA512');
    qz.security.setSignaturePromise(function (toSign) {
      return function (resolve, reject) {
        return getSignature(toSign)
          .then((response) => resolve(response.signature))
          .catch(reject);
      };
    });

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const connect = useCallback(() => {
    const isActive = qz.websocket.isActive();
    if (!isActive) {
      if (isMountedRef.current) {
        setPrinterState((printersState) => ({
          ...printersState,
          status: printerStatus.connecting
        }));
      }

      qz.websocket
        .connect()
        .then(qz.printers.find)
        .then((printers) => {
          if (isMountedRef.current) {
            setPrinterState({ status: printerStatus.connected, printers });
          }
        })
        .then(() => {
          if (isMountedRef.current) {
            qz.printers.setPrinterCallbacks((evt) => {
              if (
                evt.type === 'ACTION' &&
                evt.eventType === 'PRINTER' &&
                (evt.statusCode === 'idle' || evt.statusCode === 'paused')
              ) {
                qz.printers.find().then((printers) => {
                  if (isMountedRef.current) {
                    setPrinterState((printersState) => ({
                      ...printersState,
                      printers
                    }));
                  }
                });
              }
            });
            return qz.printers.startListening().then(qz.printers.getStatus);
          }
        })
        .catch(() => {
          if (isMountedRef.current) {
            setPrinterState({ status: printerStatus.disconnected, printers: [] });
          }
        });

      qz.websocket.setClosedCallbacks(() => {
        if (isMountedRef.current) {
          setPrinterState({ status: printerStatus.disconnected, printers: [] });
        }
      });
    }
  }, [setPrinterState]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      try {
        qz.printers.stopListening();
        qz.websocket.disconnect();
      } catch (error) {
        console.warn('Error during printer cleanup:', error);
      }
    };
  }, []);

  return {
    connect,
    connected: printersState.status === printerStatus.connected,
    ...printersState
  };
}

export { usePrinter };
